import { CNN_UID_STORAGE_KEY, getCnnUid } from '../utils/hydratePayload';
import { localStorageAccessible } from '../utils/browser';

/**
 * How long to keep watching for a sign-in, and how often to look.
 *
 * A poll is unavoidable here. `storage` fires only in *other* tabs, never in the
 * one that wrote the value, so the tab the user actually signs in on — the case
 * this exists for — would never hear the event. The window is bounded so a page
 * left open all day is not polling all day; a sign-in after it falls back to the
 * previous behavior of resolving on the next page load.
 */
const CNN_UID_POLL_MS = 1000;
const CNN_UID_WATCH_TIMEOUT_MS = 5 * 60 * 1000;

let watchTimer: ReturnType<typeof setInterval> | null = null;
let storageListener: ((event: StorageEvent) => void) | null = null;

/** Stop the poll and drop the cross-tab listener. Safe to call when not watching. */
export function unwatchCnnUid(): void {
  if (watchTimer !== null) {
    clearInterval(watchTimer);
    watchTimer = null;
  }
  if (storageListener) {
    window.removeEventListener('storage', storageListener);
    storageListener = null;
  }
}

/**
 * Call `onCnnUid` as soon as a CNN uid appears, so signing in resolves a Tier 1
 * token in the page view the user signed in on rather than the next one.
 *
 * One-shot and idempotent: the first uid seen ends the watch, and a second call
 * while a watch is live does nothing.
 */
export function watchCnnUid(onCnnUid: (cnnUid: string) => void): void {
  if (watchTimer !== null || storageListener) {
    return;
  }
  if (!localStorageAccessible()) {
    return;
  }
  // Already signed in, so whoever is initializing has the uid and Tier 1 is
  // already covered by the caller's own resolve.
  if (getCnnUid()) {
    return;
  }

  const check = (): boolean => {
    const cnnUid = getCnnUid();
    if (!cnnUid) {
      return false;
    }
    unwatchCnnUid();
    onCnnUid(cnnUid);
    return true;
  };

  // Catches a sign-in in another tab, where it beats the poll and costs nothing.
  storageListener = (event: StorageEvent) => {
    if (event.key === CNN_UID_STORAGE_KEY) {
      check();
    }
  };
  window.addEventListener('storage', storageListener);

  const deadline = Date.now() + CNN_UID_WATCH_TIMEOUT_MS;
  watchTimer = setInterval(() => {
    if (check()) {
      return;
    }
    if (Date.now() >= deadline) {
      unwatchCnnUid();
    }
  }, CNN_UID_POLL_MS);
}
